import React, { useState } from 'react';
import { ShoppingBag, Heart, X } from 'lucide-react';
import { formatPrice } from '../lib/format';
import { resolveImageUrl } from '../lib/images';
import SizeGuidePanel from './SizeGuidePanel';

const ProductPage = ({ product, theme, onAddToCart, onToggleWishlist, isWishlisted }) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [currentImageType, setCurrentImageType] = useState('front');

  const sizes = product.sizes?.length
    ? product.sizes
    : ['S', 'M', 'L', 'XL', 'XXL'].map((s) => ({ size: s, stock: 10 }));

  const sizeGuideCategory = (() => {
    const haystack = `${product.name || ''} ${product.type || ''} ${product.subCategory || ''}`.toLowerCase();
    if (/(hood|jacket|outer|sweat|crew|zip)/.test(haystack)) return 'hoodies';
    if (/(pant|trouser|short|jogger|bottom|cargo|jean)/.test(haystack)) return 'pants';
    return 'tees';
  })();

  const availableImages = {
    front: resolveImageUrl(product.images?.front?.url),
    back: resolveImageUrl(product.images?.back?.url),
    closeup: resolveImageUrl(product.images?.closeup?.url),
    folded: resolveImageUrl(product.images?.folded?.url),
    streetwear: (product.images?.streetwear || []).map((img) => ({
      ...img,
      url: resolveImageUrl(img.url),
    })),
  };

  const getCurrentImageUrl = () => {
    if (currentImageType?.startsWith('streetwear-')) {
      const idx = parseInt(currentImageType.split('-')[1], 10);
      return availableImages.streetwear[idx]?.url || null;
    }
    if (currentImageType === 'streetwear') {
      return availableImages.streetwear[0]?.url || null;
    }
    return availableImages[currentImageType] || null;
  };

  const getThemeStyles = () => {
    switch (theme?.id) {
      case 'anime':
        return {
          container: 'bg-yellow-300 text-blue-900 font-sans tracking-tighter',
          imageContainer:
            'border-4 border-black shadow-[8px_8px_0px_black] transform -rotate-1 hover:rotate-0 transition-transform duration-300',
          title: 'text-4xl md:text-5xl font-black uppercase italic transform -skew-x-6 mb-6',
          price:
            'text-3xl font-bold bg-pink-500 text-white px-4 py-2 inline-block transform rotate-2 border-4 border-black mt-2',
          button:
            'bg-blue-600 hover:bg-blue-500 text-white border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all uppercase font-black text-xl py-4',
          sizeGuideBtn:
            'bg-blue-600 hover:bg-blue-500 text-white border-2 border-black shadow-[2px_2px_0px_black] hover:shadow-none transition-all uppercase font-black text-sm py-2 px-4',
          sizeBtn: (sel) =>
            `border-2 border-black font-bold p-3 ${sel ? 'bg-black text-white' : 'bg-white text-black hover:bg-blue-200'}`,
          thumbBtn: (sel) =>
            `w-20 h-20 overflow-hidden border-4 ${sel ? 'border-white bg-pink-500' : 'border-black bg-white'}`,
        };
      case 'tantra':
        return {
          container: 'bg-neutral-900 text-red-600 font-serif tracking-widest uppercase',
          imageContainer:
            'border-4 border-red-600 shadow-[0px_0px_20px_rgba(220,38,38,0.4)] transform -rotate-1 hover:rotate-0 transition-transform duration-300',
          title: 'text-4xl md:text-5xl font-bold border-b-4 border-red-600 pb-4',
          price: 'text-3xl font-light mt-4 text-white',
          button:
            'bg-red-900 hover:bg-red-800 text-white border-2 border-red-500 uppercase tracking-[0.2em] py-4 transition-colors duration-500',
          sizeGuideBtn:
            'bg-red-900 hover:bg-red-800 text-white border border-red-500 uppercase tracking-[0.2em] py-2 px-4 text-xs transition-colors duration-500',
          sizeBtn: (sel) =>
            `border-2 border-red-600 p-3 ${sel ? 'bg-red-900 text-white' : 'bg-black text-red-600 hover:bg-red-900/30'}`,
          thumbBtn: (sel) =>
            `w-20 h-20 border-2 overflow-hidden ${sel ? 'border-white bg-red-900' : 'border-red-600 bg-black'}`,
        };
      case 'inkspeak':
        return {
          container: 'bg-[#f0e6d2] text-black font-mono',
          imageContainer:
            'border-4 border-black grayscale transform -rotate-1 hover:rotate-0 transition-transform duration-300',
          title: 'text-3xl md:text-4xl font-bold underline decoration-4 underline-offset-4',
          price: 'text-2xl font-bold bg-black text-white px-2 py-1 inline-block mt-2',
          button:
            'bg-black text-white hover:bg-neutral-800 border-2 border-transparent hover:border-black uppercase font-bold py-4',
          sizeGuideBtn:
            'bg-black text-white hover:bg-neutral-800 border border-transparent hover:border-black uppercase font-bold py-2 px-4 text-xs',
          sizeBtn: (sel) =>
            `border-2 border-black p-3 ${sel ? 'bg-black text-white' : 'bg-transparent text-black hover:bg-black/10'}`,
          thumbBtn: (sel) =>
            `w-20 h-20 border-2 overflow-hidden ${sel ? 'border-white bg-black' : 'border-black bg-transparent'}`,
        };
      case 'soul':
        return {
          container: 'bg-indigo-100 text-indigo-900 font-sans tracking-wide',
          imageContainer:
            'rounded-3xl border-4 border-indigo-900 overflow-hidden shadow-xl transform -rotate-1 hover:rotate-0 transition-transform duration-300',
          title: 'text-4xl md:text-5xl font-bold',
          price: 'text-3xl font-medium text-indigo-600',
          button:
            'bg-indigo-600 hover:bg-indigo-500 text-white rounded-full py-4 shadow-lg hover:shadow-xl transition-all',
          sizeGuideBtn:
            'bg-indigo-600 hover:bg-indigo-500 text-white rounded-full py-2 px-4 shadow hover:shadow-md transition-all text-sm',
          sizeBtn: (sel) =>
            `rounded-full border-2 border-indigo-200 p-2 w-12 h-12 flex items-center justify-center ${sel ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-900 hover:bg-indigo-50'}`,
          thumbBtn: (sel) =>
            `w-20 h-20 rounded-xl border-2 overflow-hidden ${sel ? 'border-orange-400 bg-indigo-600' : 'border-indigo-900 bg-white'}`,
        };
      default:
        return {
          container: 'bg-white text-black',
          imageContainer:
            'border-4 border-black shadow-[16px_16px_0px_gray] transform -rotate-1 hover:rotate-0 transition-transform duration-300 overflow-hidden',
          title: 'text-4xl font-bold',
          price: 'text-2xl',
          button: 'bg-black text-white border-4 border-black py-2 px-4',
          sizeGuideBtn: 'bg-black text-white border-2 border-black py-1 px-3 text-sm font-bold uppercase',
          sizeBtn: (sel) => `border-4 border-black p-3 ${sel ? 'bg-black text-white' : 'bg-white'}`,
          thumbBtn: (sel) => `w-16 h-16 border-4 overflow-hidden ${sel ? 'border-yellow-400 bg-yellow-400' : 'border-black bg-white'}`,
        };
    }
  };

  const styles = getThemeStyles();
  const currentImage = getCurrentImageUrl();

  const handleAddToCart = () => {
    const size = selectedSize || sizes[0]?.size || 'L';
    onAddToCart(product, { size });
  };

  const isOOS = product.totalStock === 0 || product.inStock === false;

  return (
    <div className={`w-full flex flex-col ${styles.container} animate-in fade-in duration-500`}>
      <div className="flex-grow max-w-7xl mx-auto w-full p-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
        {/* Left: image gallery */}
        <div className="w-full max-w-md mx-auto flex flex-col gap-4 relative z-0">
          <div
            className={`relative aspect-square w-full ${styles.imageContainer} group cursor-zoom-in overflow-hidden shadow-xl`}
            onClick={() => setIsZoomed(!isZoomed)}
          >
            {currentImage ? (
              <img
                src={currentImage}
                alt={product.name}
                className={`w-full h-full object-cover transition-transform duration-500 ${isZoomed ? 'scale-150' : 'group-hover:scale-105'}`}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="absolute inset-0 flex items-center justify-center opacity-10 text-[20rem] font-black overflow-hidden select-none pointer-events-none">
                  {product.name?.charAt(0)}
                </div>
              </div>
            )}

            {product.tags?.length > 0 && (
              <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 font-bold z-20 shadow-lg rotate-3 border-2 border-black">
                {product.tags[0]}
              </div>
            )}

            {isOOS && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-30">
                <div className="bg-white text-black px-6 py-3 font-black uppercase tracking-widest border-4 border-black">
                  Sold Out
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pb-3 justify-start">
            {availableImages.front && (
              <button
                onClick={() => setCurrentImageType('front')}
                className={`flex-shrink-0 ${styles.thumbBtn(currentImageType === 'front')}`}
                aria-label="Front view"
              >
                <img src={availableImages.front} alt="Front" className="w-full h-full object-cover" />
              </button>
            )}
            {availableImages.back && (
              <button
                onClick={() => setCurrentImageType('back')}
                className={`flex-shrink-0 ${styles.thumbBtn(currentImageType === 'back')}`}
                aria-label="Back view"
              >
                <img src={availableImages.back} alt="Back" className="w-full h-full object-cover" />
              </button>
            )}
            {availableImages.closeup && (
              <button
                onClick={() => setCurrentImageType('closeup')}
                className={`flex-shrink-0 ${styles.thumbBtn(currentImageType === 'closeup')}`}
                aria-label="Closeup"
              >
                <img src={availableImages.closeup} alt="Closeup" className="w-full h-full object-cover" />
              </button>
            )}
            {availableImages.folded && (
              <button
                onClick={() => setCurrentImageType('folded')}
                className={`flex-shrink-0 ${styles.thumbBtn(currentImageType === 'folded')}`}
                aria-label="Folded"
              >
                <img src={availableImages.folded} alt="Folded" className="w-full h-full object-cover" />
              </button>
            )}
            {availableImages.streetwear.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageType(`streetwear-${idx}`)}
                className={`flex-shrink-0 ${styles.thumbBtn(currentImageType === `streetwear-${idx}`)}`}
                aria-label={`Look ${idx + 1}`}
              >
                <img src={img.url} alt={`Look ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Right: details */}
        <div className="flex flex-col gap-8 sticky top-24 w-full relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-6 text-sm font-black text-black uppercase tracking-widest">
              <span>{product.type}</span>
              <span>/</span>
              <span>{isOOS ? 'SOLD OUT' : 'IN STOCK'}</span>
            </div>
            <h1 className={styles.title}>{product.name}</h1>
            <div className={styles.price}>{formatPrice(product.salePrice ?? product.price)}</div>
            {product.salePrice && product.salePrice < product.price && (
              <span className="ml-3 line-through opacity-50 font-mono">{formatPrice(product.price)}</span>
            )}
          </div>

          <div className="prose prose-lg font-bold max-w-none">
            <p>
              {product.description ||
                `Premium heavyweight cotton. Designed in the void. Limited run. Do not sleep.`}
            </p>
            <ul className="list-disc pl-5 mt-6 space-y-3 font-bold">
              <li>100% Organic Cotton (400gsm)</li>
              <li>Oversized Fit</li>
              <li>Screen Printed by Hand</li>
              <li>Pre-shrunk</li>
            </ul>
          </div>

          {/* Sizes */}
          <div>
            <div className="font-bold mb-4 flex justify-between items-center">
              <span>SELECT SIZE</span>
              <button className={styles.sizeGuideBtn} onClick={() => setIsSizeGuideOpen(true)}>
                Size Guide
              </button>
            </div>
            <div className="flex gap-4 flex-wrap">
              {sizes.map((s) => {
                const sizeName = typeof s === 'string' ? s : s.size;
                const sizeStock = typeof s === 'string' ? 10 : (s.stock ?? 0);
                const oos = sizeStock <= 0;
                return (
                  <button
                    key={sizeName}
                    onClick={() => !oos && setSelectedSize(sizeName)}
                    disabled={oos}
                    className={`relative w-14 h-14 flex items-center justify-center transition-all ${styles.sizeBtn(selectedSize === sizeName)} ${oos ? 'opacity-30 cursor-not-allowed line-through' : ''}`}
                  >
                    {sizeName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleAddToCart}
              disabled={isOOS}
              className={`flex-grow flex items-center justify-center gap-3 ${styles.button} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <ShoppingBag size={24} />
              {isOOS ? 'SOLD OUT' : 'ADD TO CART'}
            </button>
            {onToggleWishlist && (
              <button
                onClick={() => onToggleWishlist(product)}
                aria-label="Toggle wishlist"
                className="p-4 border-4 border-black bg-white text-black hover:bg-black hover:text-white transition-colors"
              >
                <Heart size={24} fill={isWishlisted ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>

          <div className="text-xs opacity-50 text-center mt-8 font-medium tracking-wide">
            FREE SHIPPING IN INDIA ON ORDERS OVER ₹999 | COD AVAILABLE
          </div>
        </div>
      </div>

      {/* Inline size guide modal */}
      {isSizeGuideOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto"
          onClick={() => setIsSizeGuideOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl my-8 bg-neutral-100 border-4 border-black shadow-[10px_10px_0px_rgba(0,0,0,1)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsSizeGuideOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white border-2 border-black hover:bg-yellow-300 transition-colors"
              aria-label="Close size guide"
            >
              <X size={20} />
            </button>

            <div className="p-6 md:p-8">
              <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter">Size Guide</h2>
              <p className="text-neutral-600 text-sm font-mono mt-2 mb-8">
                Find your perfect fit across all Realms. Measurement tolerances are &plusmn;1 inch.
              </p>
              <SizeGuidePanel defaultCategory={sizeGuideCategory} theme={theme} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;
